import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from '../../components/Navbar/Navbar';
import InsuranceBanner from '../../components/InsuranceBanner/InsuranceBanner';
import PageTitle from '../../components/pagetitle/PageTitle';
import Footer from '../../components/footer/Footer';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Logo from '../../images/logo-2.svg';
import './InternationalPage.css';
import drPereiraImage from '../../images/international/dr-pereira-e.png';
import heroInternational from '../../images/international/hero.jpg';
import InternationalForm from '../../components/InternationalForm/InternationalForm';

const InternationalPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const treatments = [
        {
            title: "Wharton's Jelly Mesenchymal Stem Cell Therapy",
            description: "Advanced cellular regeneration using ethically sourced stem cells",
            icon: "fa-microscope"
        },
        {
            title: "Neurological Recovery Protocols",
            description: "Specialized treatments for brain and nerve conditions",
            icon: "fa-brain"
        },
        {
            title: "Autism Spectrum Treatments",
            description: "Breakthrough protocols for developmental conditions",
            icon: "fa-child-reaching"
        },
        {
            title: "Anti-Aging and Longevity Treatments",
            description: "Comprehensive regenerative wellness programs",
            icon: "fa-lightbulb"
        },
        {
            title: "Exosome Therapies",
            description: "Next-generation cellular communication therapy",
            icon: "fa-vial"
        },
        {
            title: "Joint Regeneration Treatments",
            description: "Non-surgical solutions for arthritis and joint pain",
            icon: "fa-bone"
        },
        {
            title: "Advanced PRP and BMAC Protocols",
            description: "Concentrated healing factors from your own body",
            icon: "fa-syringe"
        }
    ];

    const processSteps = [
        {
            number: "01",
            title: "Initial Consultation",
            description: "Virtual consultation with Dr. Pereira from your home country"
        },
        {
            number: "02",
            title: "Treatment Planning",
            description: "Customized protocol development based on your specific needs"
        },
        {
            number: "03",
            title: "Travel Coordination",
            description: "Our partner clinic handles all logistics and arrangements"
        },
        {
            number: "04",
            title: "Treatment Week",
            description: "Dr. Pereira arrives in Antigua for your procedures"
        },
        {
            number: "05",
            title: "Recovery Support",
            description: "On-site medical support in paradise setting"
        },
        {
            number: "06",
            title: "Follow-up Care",
            description: "Continued monitoring and guidance post-treatment"
        }
    ];

    const targetPatients = [
        "Patients unable to obtain US visas",
        "International patients preferring Caribbean location",
        "Those seeking vacation-style recovery environment",
        "Patients from countries with limited regenerative medicine access",
        "Individuals wanting Dr. Pereira's expertise internationally"
    ];

    return (
        <div className="international-page-wrapper">
            <Helmet>
                <title>Caribbean Stem Cell Therapy in Antigua | Dr. Eugene Pereira | International Regenerative Medicine</title>
                <meta name="description" content="Dr. Eugene Pereira provides advanced stem cell therapy in Antigua for international patients. World-class regenerative medicine in the Caribbean without US visa requirements. Same expertise, Caribbean accessibility." />
                <meta name="keywords" content="stem cell therapy Antigua, Caribbean stem cell therapy, international stem cell therapy, stem cell treatment Caribbean, Dr. Pereira Antigua, regenerative medicine Caribbean, Antigua medical tourism, Caribbean regenerative medicine" />
                <meta property="og:title" content="Advanced Stem Cell Therapy in the Caribbean - Dr. Eugene Pereira" />
                <meta property="og:description" content="World-class stem cell treatments in Antigua. No US visa required. Dr. Pereira personally travels to perform your treatment." />
                <meta property="og:type" content="website" />
                <link rel="canonical" href="https://ampedtherapeutics.com/international" />
            </Helmet>

            <Navbar hclass={'wpo-site-header wpo-site-header-s2'} Logo={Logo} />
            <InsuranceBanner />
            <div className="international-hero-wrapper" style={{
                backgroundImage: `url(${heroInternational})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}>
                <PageTitle pageTitle="International Patients" pagesub="Caribbean Stem Cell Therapy" />
            </div>

            {/* Hero Content Section */}
            <section className="international-intro-section section-padding">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-10 mx-auto">
                            <div className="intro-content text-center">
                                <h2 className="section-main-title">Advanced Stem Cell Therapy in the Caribbean</h2>
                                <p className="lead-text">Dr. Eugene Pereira brings world-class regenerative medicine directly to Antigua for international patients</p>
                                <div className="highlight-box">
                                    <span className="highlight-text">Same Advanced Florida Treatments, Caribbean Accessibility</span>
                                </div>
                                <div className="intro-cta">
                                    <a href="#international-form" className="theme-btn">Schedule Caribbean Consultation</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Caribbean Treatment */}
            <section className="why-caribbean-section section-padding">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h2>Caribbean Excellence</h2>
                                <span>World-Class Care in Paradise</span>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-4 col-md-6">
                            <div className="caribbean-feature-card">
                                <div className="feature-icon">
                                    <i className="fa fa-passport"></i>
                                </div>
                                <h3>No US Visa Requirements</h3>
                                <p>Access advanced treatments without visa complications</p>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="caribbean-feature-card">
                                <div className="feature-icon">
                                    <i className="fa fa-umbrella-beach"></i>
                                </div>
                                <h3>Beautiful Recovery Environment</h3>
                                <p>Heal in the pristine Caribbean paradise of Antigua</p>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="caribbean-feature-card">
                                <div className="feature-icon">
                                    <i className="fa fa-microscope"></i>
                                </div>
                                <h3>Same Advanced Therapies</h3>
                                <p>Identical protocols to our Florida practice</p>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="caribbean-feature-card">
                                <div className="feature-icon">
                                    <i className="fa fa-user-md"></i>
                                </div>
                                <h3>Dr. Pereira Personally Travels</h3>
                                <p>Your treatment performed by Dr. Pereira himself</p>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="caribbean-feature-card">
                                <div className="feature-icon">
                                    <i className="fa fa-language"></i>
                                </div>
                                <h3>Multilingual Environment</h3>
                                <p>Spanish-speaking staff available for South American patients - Clear communication throughout your journey</p>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="caribbean-feature-card">
                                <div className="feature-icon">
                                    <i className="fa fa-hospital"></i>
                                </div>
                                <h3>Established Medical Tourism</h3>
                                <p>Infrastructure designed for international patients</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Dr. Pereira's Personal Service */}
            <section className="personal-service-section">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <div className="dr-pereira-image-wrapper">
                                <img src={drPereiraImage} alt="Dr. Eugene Pereira" className="dr-image" />
                                <div className="experience-badge">
                                    <span className="years">25+</span>
                                    <span className="text">Years Experience</span>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="personal-service-content">
                                <div className="section-title">
                                    <h2>Personal Attention</h2>
                                    <span>Dr. Pereira Comes to You</span>
                                </div>
                                <div className="quote-highlight">
                                    <i className="flaticon-quote"></i>
                                    <p>I personally travel to Antigua to perform your treatment</p>
                                </div>
                                <ul className="service-list">
                                    <li><i className="ti-check"></i> 25+ years expertise brought directly to Caribbean</li>
                                    <li><i className="ti-check"></i> Same FIPP & ABIPP certified procedures</li>
                                    <li><i className="ti-check"></i> Personal attention throughout your journey</li>
                                    <li><i className="ti-check"></i> Continuity of care from consultation to recovery</li>
                                    <li><i className="ti-check"></i> Direct access to Dr. Pereira's expertise</li>
                                </ul>
                                <div className="credentials-highlight">
                                    <p><strong>Board Certifications:</strong> MD, ABA-PM, FIPP, ABIPP</p>
                                    <p><strong>Licensed in:</strong> Florida, New York, New Jersey, Utah</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Advanced Treatments Available */}
            <section className="treatments-section section-padding">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h2>Comprehensive Care</h2>
                                <span>Complete Regenerative Medicine Suite</span>
                                <p>All advanced therapies available in our Caribbean location</p>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        {treatments.map((treatment, index) => (
                            <div key={index} className="col-lg-6">
                                <div className="treatment-card">
                                    <div className="treatment-icon">
                                        <i className={`fa ${treatment.icon}`}></i>
                                    </div>
                                    <div className="treatment-content">
                                        <h4>{treatment.title}</h4>
                                        <p>{treatment.description}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="row mt-4">
                        <div className="col-lg-12">
                            <div className="autism-highlight-box">
                                <div className="highlight-icon">
                                    <i className="fa fa-brain"></i>
                                </div>
                                <div className="highlight-content">
                                    <h3>Breakthrough Autism Treatment</h3>
                                    <p>Dr. Pereira has successfully treated pediatric patients with autism spectrum disorders, including an 11-year-old patient who showed remarkable improvement following stem cell therapy.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Partner Facility Excellence */}
            <section className="facility-section">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h2>Medical Excellence</h2>
                                <span>State-of-the-Art Caribbean Medical Center</span>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-6">
                            <div className="facility-features">
                                <h3>Facility Capabilities</h3>
                                <ul className="feature-list">
                                    <li><i className="ti-check-box"></i> Accredited medical facility in Antigua</li>
                                    <li><i className="ti-check-box"></i> Full surgical capabilities with anesthesia</li>
                                    <li><i className="ti-check-box"></i> Image-guided procedure capabilities</li>
                                    <li><i className="ti-check-box"></i> Highest standards of sterility and safety</li>
                                    <li><i className="ti-check-box"></i> Beautiful recovery environment</li>
                                    <li><i className="ti-check-box"></i> Medical tourism support services</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="safety-standards">
                                <h3>Safety & Standards</h3>
                                <div className="standard-item">
                                    <i className="flaticon-certificate"></i>
                                    <div>
                                        <h4>International Accreditation</h4>
                                        <p>Meets global healthcare standards</p>
                                    </div>
                                </div>
                                <div className="standard-item">
                                    <i className="flaticon-shield"></i>
                                    <div>
                                        <h4>Same Safety Protocols</h4>
                                        <p>Identical to Dr. Pereira's Florida practice</p>
                                    </div>
                                </div>
                                <div className="standard-item">
                                    <i className="flaticon-medical-team"></i>
                                    <div>
                                        <h4>Expert Medical Team</h4>
                                        <p>Trained professionals supporting your care</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Treatment Process */}
            <section className="process-section section-padding">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h2>Your Journey</h2>
                                <span>Your Caribbean Treatment Journey</span>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        {processSteps.map((step, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="process-card">
                                    <div className="process-number">{step.number}</div>
                                    <h4>{step.title}</h4>
                                    <p>{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Who This Is For */}
            <section className="target-patients-section">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h2>Ideal Candidates</h2>
                                <span>Ideal for International Patients</span>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-8 mx-auto">
                            <div className="target-patients-content">
                                {targetPatients.map((patient, index) => (
                                    <div key={index} className="patient-item">
                                        <i className="ti-check"></i>
                                        <span>{patient}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="testimonials-section">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h2>Success Stories</h2>
                                <span>International Patient Testimonials</span>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-6">
                            <div className="testimonial-card">
                                <div className="testimonial-header">
                                    <div className="patient-info">
                                        <h4>Joint Pain Patient</h4>
                                        <span className="location">Canada</span>
                                    </div>
                                    <div className="rating">
                                        <i className="ti-star"></i>
                                        <i className="ti-star"></i>
                                        <i className="ti-star"></i>
                                        <i className="ti-star"></i>
                                        <i className="ti-star"></i>
                                    </div>
                                </div>
                                <div className="testimonial-content">
                                    <p>"Unable to get a US visa, I thought advanced stem cell therapy was out of reach. Dr. Pereira coming to Antigua was a blessing. The treatment facility was excellent, and recovering in the Caribbean was an added bonus."</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="testimonial-card">
                                <div className="testimonial-header">
                                    <div className="patient-info">
                                        <h4>Parent of 11-Year-Old Autism Patient</h4>
                                        <span className="location">International Patient</span>
                                    </div>
                                    <div className="rating">
                                        <i className="ti-star"></i>
                                        <i className="ti-star"></i>
                                        <i className="ti-star"></i>
                                        <i className="ti-star"></i>
                                        <i className="ti-star"></i>
                                    </div>
                                </div>
                                <div className="testimonial-content">
                                    <p>"Dr. Pereira's stem cell treatment for our son with autism has been life-changing. The improvements in his communication and social interaction have exceeded our expectations. Traveling to Antigua was seamless, and Dr. Pereira's personal attention made all the difference."</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <InternationalForm />

            <Footer hclass={'wpo-site-footer'} />
            <Scrollbar />
        </div>
    );
};

export default InternationalPage;